import {useMemo} from 'react';
import {Button, ChatSurfaceVariantProvider, Icons} from '@continuum/ui';
import type {UseChatResult} from '@continuum/ui/hooks';
import type {Canvas, DocumentsBlock} from '~/domains/canvas';
import {AskAiPanel} from './ask-ai-panel';
import {CanvasPanelHeader} from './canvas-panel';

interface Props {
  chat: UseChatResult;
  canvas: Canvas;
  onClose: () => void;
}

function useChatIntro(canvas: Canvas) {
  return useMemo(() => {
    const docBlock = canvas.blocks.find(
      (b): b is DocumentsBlock => b.__typename === 'DocumentsBlock',
    );
    const hasDocuments = !!docBlock && docBlock.documents.length > 0;
    const hasChartOrMetric = canvas.blocks.some(
      (b) => b.__typename === 'ChartBlock' || b.__typename === 'MetricBlock',
    );
    const advisorFirstName = canvas.advisor?.name?.split(' ')[0];

    const suggestions: string[] = [
      'What were the key topics discussed?',
      'What are my next steps?',
    ];
    if (hasDocuments) {
      suggestions.push('What are the key takeaways from the documents?');
    }
    if (hasChartOrMetric) {
      suggestions.push('Explain the trends shown here');
    }

    const subject = hasDocuments
      ? 'this page and the attached documents'
      : 'this page';
    const suffix = advisorFirstName ? ` ${advisorFirstName} shared` : '';

    return {
      advisorFirstName,
      suggestions: suggestions.slice(0, 4),
      contextDescription: `I have context on ${subject}${suffix}. Ask me anything.`,
    };
  }, [canvas.blocks, canvas.advisor]);
}

export const ChatPanelContent = ({chat, canvas, onClose}: Props) => {
  const {advisorFirstName, suggestions, contextDescription} =
    useChatIntro(canvas);

  return (
    <>
      <CanvasPanelHeader
        title="Ask about this page"
        subtitle={contextDescription}
        onClose={onClose}
        closeLabel="Close chat"
        actions={
          chat.messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={chat.reset}
              aria-label="Reset chat"
              tooltip="Reset"
              tooltipOptions={{side: 'bottom'}}
              className="rounded-full text-muted-foreground"
            >
              <Icons.rotateCcw className="size-4" />
            </Button>
          )
        }
      />
      <div className="min-h-0 flex-1 overflow-hidden">
        <ChatSurfaceVariantProvider
          value={canvas.agentRuntimeChat ? 'agent' : 'default'}
        >
          <AskAiPanel
            chat={chat}
            suggestions={suggestions}
            advisorName={advisorFirstName}
            className="h-full border-0 p-0"
          />
        </ChatSurfaceVariantProvider>
      </div>
    </>
  );
};
